import { useState, useEffect, useMemo, useRef, FormEvent, KeyboardEvent } from 'react';
import { createFilterOptions, updateActiveTask, Util } from '../../util/Util';
import { BomItem, BomRole, useBom } from './BomContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import RefreshButton from '../../components/RefreshButton';
import QuantityStepperInput from '../../components/QuantityStepperInput';
import { focusAndSelect, isPlainEnter } from '../../util/focusField';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { useAuth } from '../../hooks/useAuth';
import { MYSQL_LIMITS } from '../../util/constants';
import { MONEY_LIMITS, QTY_LIMITS, isWholeNumber, qtyMin } from '../../util/quantity';
import {
	Autocomplete,
	Button,
	Checkbox,
	Grid,
	MenuItem,
	TextField,
} from '@mui/material';
import AsynchronousPrice from '../../components/PriceAutoComplete';
import { renderCatalogItemOption } from '../../components/itemAutocompleteRenderOption';
import {
	catalogItemAutocompleteInteractionProps,
	handleCatalogItemAutocompleteChange,
	isCatalogItemOptionEqualToValue,
} from '../../components/catalogItemAutocomplete';
import { applySavedCatalogOption, CatalogItemPickerProvider } from '../../components/CatalogItemActions';
import { isItemMetered } from '../../components/quantityStepperStock';
import type { CatalogItemRow } from '../inventory/ItemDetailModal';
import { Add } from '@mui/icons-material';
import { toast } from 'react-toastify';
import VirtualizedListbox from '../../components/VirtualizedListbox';

import { roundQty } from "../../util/quantity";
interface Props {
	bundle?: any;
	readOnly?: boolean;
}

const BomItemDetail = ({ readOnly = false }: Props) => {
	const [barcodes, setBarcodes] = useState<any>({});
	const [selectedItem, setSelectedItem] = useState<BomItem | null>(null);
	const [errorInput, setErrorInput] = useState('');
	const [isItemLoading, setIsItemLoading] = useState(false);
	const [isPriceLoading, setIsPriceLoading] = useState(false);
	const [isUnitLoading, setIsUnitLoading] = useState(false);
	const [prices, setPrices] = useState<any[]>([]);
	const [unitOptions, setUnitOptions] = useState<string[]>([]);
	const [useBarcode, setUseBarcode] = useState(false);
	const [barcode, setBarcode] = useState('');
	const [role, setRole] = useState<BomRole>('INPUT');

	const { setPendingTasks, activeTask, items, setItems, apiRef } = useBom();
	const { queueReRun, t } = useUI();
	const { appSettings } = useAppSocket();
	const { api } = useAuth();

	const barcodeRef = useRef<HTMLInputElement>(null);
	const itemRef = useRef<HTMLInputElement>(null);
	const quantityRef = useRef<HTMLInputElement>(null);
	const priceRef = useRef<HTMLInputElement>(null);
	const unitRef = useRef<HTMLInputElement>(null);
	const errorMessageRef = useRef('');

	/**
	 * Production increases real depot stock, so an output must be stock-tracked —
	 * an unmetered item has nowhere to land. Inputs carry no such restriction.
	 */
	const meteredItems = useMemo(
		() => items.filter((item: any) => isItemMetered(item, true)),
		[items],
	);
	const pickableItems = role === 'OUTPUT' ? meteredItems : items;

	const rejectUnmeteredOutput = () => {
		toast.error(t('common:production.outputMustBeMetered'));
		setErrorInput('itemInput');
		setTimeout(() => setErrorInput(''), 2000);
	};

	const fetchItems = async () => {
		const depotId = activeTask?.depot || -1;
		if (depotId < 1) {
			setItems([]);
			setBarcodes({});
			return;
		}
		setIsItemLoading(true);
		// A BOM is a recipe, not a movement: the whole active catalog is fair game,
		// including unmetered items (labour, services) that are valid inputs but
		// have no stock. The depot still resolves per-depot prices.
		const res = await api.get(`depots/${depotId}/items`, {
			params: {
				IncludeCatalog: true,
				IncludeUnmetered: true,
			}
		});
		if (res?.status === 200) {
			setItems(res.data.items || []);
			setBarcodes(res.data.barcodes || {});
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchItems);
		}
		setIsItemLoading(false);
	};

	const fetchPrices = async () => {
		setPrices([]);
		const itemId = selectedItem?.id || -1;
		if (itemId < 1) return;
		setIsPriceLoading(true);
		const res = await api.get(`items/${itemId}/price_history`);
		if (res?.status === 200) {
			setPrices(res.data);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchPrices);
		}
		setIsPriceLoading(false);
	};

	const fetchUnits = async (itemId: number) => {
		setUnitOptions([]);
		if (itemId < 1) return;
		setIsUnitLoading(true);
		const res = await api.get(`items/${itemId}/unit_history`);
		if (res?.status === 200) {
			const list = (res.data.units || []).map((u: any) => u.unit as string);
			setUnitOptions(list);
			// Prefer first historical unit for this item
			if (list.length > 0) {
				setSelectedItem((prev) =>
					prev ? new BomItem({ ...prev, unit: prev.unit || list[0] }) : prev
				);
			}
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(() => fetchUnits(itemId));
		}
		setIsUnitLoading(false);
	};

	const handleCatalogSaved = (updated: CatalogItemRow) => {
		setItems((prev) => applySavedCatalogOption(prev, updated));
		setSelectedItem((prev) =>
			prev && prev.id === updated.id
				? new BomItem({
					...prev,
					item: updated.ItemName || prev.item,
					item_code: updated.ItemCode || prev.item_code,
					isFractional: updated.IsFractional === true,
				})
				: prev
		);
		updateActiveTask(setPendingTasks, (prev) => ({
			...prev,
			items: prev.items?.map((it) =>
				it.id === updated.id
					? new BomItem({
						...it,
						item: updated.ItemName || it.item,
						item_code: updated.ItemCode || it.item_code,
						isFractional: updated.IsFractional === true,
					})
					: it
			),
		}));
	};

	const onItemChanged = (item_id: number) => {
		const item: any = items.find((i: any) => i.id === item_id);
		if (item) {
			const myItem = new BomItem({
				id: item.id,
				item_code: item.ItemCode,
				item: item.ItemName,
				price: item.CostPrice ?? 0,
				stock: item.Stock ?? 0,
				quantity: selectedItem?.quantity || 1,
				role,
				unit: selectedItem?.unit || '',
				category: item.Category,
				isFractional: item.IsFractional === true,
			});
			setSelectedItem(myItem);
			fetchUnits(item.id);
			focusAndSelect(quantityRef);
		} else {
			setSelectedItem(null);
			setPrices([]);
			setUnitOptions([]);
		}
	};

	const lookupBarcode = (code: string = barcode) => {
		const itemID = barcodes[code];
		const scanned = items.find((i: any) => i.id === itemID);
		if (itemID && scanned && role === 'OUTPUT' && !isItemMetered(scanned, true)) {
			rejectUnmeteredOutput();
			return;
		}
		if (itemID) {
			onItemChanged(itemID);
			focusAndSelect(quantityRef);
		} else {
			itemRef.current?.focus();
			setErrorInput('barcodeInput');
			setTimeout(() => setErrorInput(''), 2000);
		}
	};

	const validateQty = (value: number): boolean => {
		if (value < qtyMin(!!selectedItem?.isFractional) || value > QTY_LIMITS.max) {
			errorMessageRef.current = t('common:validation.quantityRange', {
				max: Util.formatNumber(QTY_LIMITS.max),
			});
			setErrorInput('quantityInput');
			setTimeout(() => {
				setErrorInput('');
				errorMessageRef.current = '';
			}, 2000);
			return false;
		}
		if (!selectedItem?.isFractional && !isWholeNumber(value)) {
			errorMessageRef.current = t('common:validation.quantityMustBeWhole');
			setErrorInput('quantityInput');
			setTimeout(() => {
				setErrorInput('');
				errorMessageRef.current = '';
			}, 2000);
			return false;
		}
		return true;
	};

	const validateAmt = (value: number): boolean => {
		if (isNaN(value) || value < 0 || value > MONEY_LIMITS.max) {
			setErrorInput('amountInput');
			setTimeout(() => setErrorInput(''), 2000);
			return false;
		}
		return true;
	};

	const onAddClicked = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (readOnly || !selectedItem || !activeTask) return;
		if (role === 'OUTPUT') {
			const catalogEntry = items.find((i: any) => i.id === selectedItem.id);
			if (!isItemMetered(catalogEntry, true)) {
				rejectUnmeteredOutput();
				return;
			}
		}
		if (!validateQty(selectedItem.quantity) || !validateAmt(selectedItem.price))
			return;

		const line = new BomItem({
			...selectedItem,
			role,
			item: selectedItem.item?.trim() || selectedItem.item,
		});

		const existingIndex = activeTask.items!.findIndex((r) =>
			Util.compareStrings(r.uniqueId, line.uniqueId)
		);

		let merged = new BomItem({ ...line });
		if (existingIndex > -1) {
			merged.quantity = roundQty(merged.quantity + activeTask.items![existingIndex].quantity);
			if (apiRef.current) {
				const rowIndex =
					apiRef.current.getRowIndexRelativeToVisibleRows(merged.uniqueId) + 1;
				if (merged.quantity > QTY_LIMITS.max) {
					errorMessageRef.current = t('common:validation.mergeExceedsLimit', {
						row: rowIndex,
						max: Util.formatNumber(QTY_LIMITS.max),
					});
					setErrorInput('quantityInput');
					return;
				}
				toast.info(
					t('common:info.mergedWithRow', {
						row: rowIndex,
						quantity: merged.quantity,
					})
				);
			}
		}

		updateActiveTask(setPendingTasks, (prev) => {
			const updated =
				existingIndex > -1
					? prev.items!.map((it, idx) =>
						idx === existingIndex ? merged : it
					)
					: [...(prev.items || []), merged];
			return { ...prev, items: updated };
		});

		focusAndSelect(useBarcode ? barcodeRef : itemRef);
	};

	useEffect(() => {
		fetchItems();
		setSelectedItem(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTask?.depot]);

	useEffect(() => {
		setUseBarcode(
			Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_BARCODE?.value, true)
		);
	}, [appSettings]);

	useEffect(() => {
		if (!selectedItem) return;

		const catalogEntry = items.find((i: any) => i.id === selectedItem.id);
		if (role === 'OUTPUT' && !isItemMetered(catalogEntry, true)) {
			// Unmetered item cannot be produced: drop the selection rather than
			// carry an invalid line into the form.
			rejectUnmeteredOutput();
			setSelectedItem(null);
			setPrices([]);
			setUnitOptions([]);
			itemRef.current?.focus();
			return;
		}

		setSelectedItem(new BomItem({ ...selectedItem, role }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [role]);

	// Enter in quantity moves to the price; Enter there adds the line (form submit).
	const onQuantityKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (!isPlainEnter(event)) return;
		event.preventDefault();
		focusAndSelect(priceRef);
	};

	return (
		<form onSubmit={onAddClicked} style={{ marginBottom: 10 }}>
			<Grid container rowSpacing={0} columnSpacing={0}>
				<Grid size={{ xs: 12, sm: 6, md: 2 }}>
					<TextField
						id="barcodeInput"
						size="small"
						label={
							<span>
								{t('common:ui.labels.barcode')}&nbsp;&nbsp;
								<Checkbox
									checked={useBarcode}
									onChange={() => setUseBarcode(!useBarcode)}
									disabled={readOnly}
								/>
							</span>
						}
						variant="filled"
						fullWidth
						type="text"
						value={barcode}
						inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
						inputRef={barcodeRef}
						slotProps={{
							input: {
								endAdornment: (
									<FontAwesomeIcon
										icon={faMagnifyingGlass}
										onClick={() => lookupBarcode()}
									/>
								),
							},
						}}
						onFocus={(e) => e.target.select()}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								lookupBarcode(barcode);
							}
						}}
						onChange={(e) => setBarcode(e.target.value)}
						disabled={
							readOnly ||
							!activeTask ||
							(activeTask?.depot || -1) < 1 ||
							isItemLoading ||
							Object.keys(barcodes).length === 0
						}
						error={errorInput === 'barcodeInput'}
						helperText={
							errorInput === 'barcodeInput'
								? t('common:stockTrace.barcodeNotFound')
								: ''
						}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 3 }}>
					<CatalogItemPickerProvider onItemSaved={handleCatalogSaved}>
					<Autocomplete
						id="itemInput"
						size="small"
						options={pickableItems}
						ListboxComponent={VirtualizedListbox}
						filterOptions={createFilterOptions((opt) => opt.ItemName || '')}
						{...catalogItemAutocompleteInteractionProps}
						getOptionLabel={(item) => item.ItemName || ''}
						isOptionEqualToValue={isCatalogItemOptionEqualToValue}
						renderOption={(props, option) =>
							renderCatalogItemOption(props, option, 'both')
						}
						value={pickableItems.find((i: any) => i.id === selectedItem?.id) || null}
						onChange={(_, newValue, reason) => {
							handleCatalogItemAutocompleteChange(reason, newValue, {
								onSelect: (id) => onItemChanged(id),
								onClear: () => onItemChanged(-1),
							});
						}}
						loading={isItemLoading}
						disabled={readOnly || !activeTask || (activeTask?.depot || -1) < 1}
						renderInput={(params) => (
							<TextField
								{...params}
								label={
									<span>
										{t('common:ui.labels.item')}&nbsp;&nbsp;
										<RefreshButton
											isWorking={isItemLoading}
											onClick={fetchItems}
											disabled={isItemLoading || (activeTask?.depot || -1) < 1}
										/>
									</span>
								}
								placeholder={
									(activeTask?.depot || -1) < 1
										? t('common:production.selectDepotFirst')
										: isItemLoading
											? t('common:ui.placeholders.loadingItems')
											: t('common:ui.placeholders.selectItem')
								}
								required
								onFocus={(e) => e.target.select()}
								inputRef={itemRef}
								variant="filled"
								fullWidth
								error={errorInput === 'itemInput'}
								helperText={
									errorInput === 'itemInput'
										? t('common:production.outputMustBeMetered')
										: undefined
								}
							/>
						)}
					/>
					</CatalogItemPickerProvider>
				</Grid>
				<Grid size={{ xs: 6, sm: 3, md: 1 }}>
					<QuantityStepperInput
						value={selectedItem?.quantity ?? 1}
						min={qtyMin(!!selectedItem?.isFractional)}
						max={QTY_LIMITS.max}
						fractional={selectedItem?.isFractional}
						disabled={readOnly || !selectedItem}
						inputRef={quantityRef}
						onKeyDown={onQuantityKeyDown}
						error={errorInput === 'quantityInput'}
						helperText={
							errorInput === 'quantityInput' ? errorMessageRef.current : undefined
						}
						onChange={(qty) =>
							setSelectedItem((prev) =>
								prev ? new BomItem({ ...prev, quantity: qty }) : prev
							)
						}
						label={t('common:tables.quantity')}
						size="small"
						fullWidth
					/>
				</Grid>
				<Grid size={{ xs: 8, sm: 4, md: 1.5 }}>
					<AsynchronousPrice<BomItem>
						fetchPrices={fetchPrices}
						prices={prices}
						selectedItem={selectedItem}
						setSelectedItem={setSelectedItem}
						itemConstructor={BomItem}
						isPriceLoading={isPriceLoading}
						inputRef={priceRef}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 4, md: 1.5 }}>
					<TextField
						select
						size="small"
						fullWidth
						variant="filled"
						label={t('common:production.role')}
						value={role}
						disabled={readOnly || !activeTask}
						onChange={(e) => setRole(e.target.value as BomRole)}
					>
						<MenuItem value="INPUT">{t('common:production.input')}</MenuItem>
						<MenuItem value="OUTPUT">{t('common:production.output')}</MenuItem>
					</TextField>
				</Grid>
				<Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
					<Autocomplete
						freeSolo
						size="small"
						options={unitOptions}
						loading={isUnitLoading}
						disabled={readOnly || !selectedItem}
						value={selectedItem?.unit || ''}
						onInputChange={(_, value) =>
							setSelectedItem((prev) =>
								prev ? new BomItem({ ...prev, unit: value }) : prev
							)
						}
						onChange={(_, value) =>
							setSelectedItem((prev) =>
								prev
									? new BomItem({
										...prev,
										unit: typeof value === 'string' ? value : value || '',
									})
									: prev
							)
						}
						renderInput={(params) => (
							<TextField
								{...params}
								variant="filled"
								label={t('common:production.unit')}
								inputRef={unitRef}
							/>
						)}
					/>
				</Grid>
				<Grid size={{ xs: 6, sm: 6, md: 1 }} display="flex" justifyContent="center" alignItems="start">
					<Button fullWidth size="large" type="submit" color="success" startIcon={<Add />} sx={{ fontWeight: 'bolder' }}>
						{t("common:ui.buttons.add")}
					</Button>
				</Grid>
			</Grid>
		</form>
	);
};

export default BomItemDetail;
